"use client";

import { useState } from "react";
import axios from "axios";
import {
  Box,
  Button,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import { Search, Save } from "@mui/icons-material";

const ProductReplacementPanel = () => {
  const [orderId, setOrderId] = useState("");
  const [orderDetails, setOrderDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Track modified products
  const [modifiedProducts, setModifiedProducts] = useState({});

  // Fetch order details
  const handleOrderSearch = async () => {
    if (!orderId.trim()) return;
    setLoading(true);
    setError("");
    setModifiedProducts({});

    try {
      // Replace with your actual API endpoint
      const response = await axios.get(
        process.env.REACT_APP_PATH + `/role1/allCapturedOrders/${orderId}`
      );
      setOrderDetails(response.data.data[0]);
    } catch (err) {
      console.error("Error fetching order:", err);
      setError(
        "Failed to fetch order details. Please check the order ID and try again."
      );
      setOrderDetails(null);
    } finally {
      setLoading(false);
    }
  };

  // Handle EAN code change
  const handleEANChange = (productId, newValue) => {
    setModifiedProducts((prev) => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        newEANCode: newValue,
        isModified: true,
      },
    }));
  };

  // Check if any products have been modified
  const hasModifiedProducts = () => {
    return Object.values(modifiedProducts).some(
      (product) => product.isModified && product.newEANCode.trim() !== ""
    );
  };

  // Submit all product replacements
  const handleSubmitChanges = async () => {
    if (!hasModifiedProducts()) return;

    setSubmitting(true);

    try {
      // Prepare data for API - only include products with changed EAN codes
      const changedProducts = Object.entries(modifiedProducts)
        .filter(
          ([_, product]) =>
            product.isModified && product.newEANCode.trim() !== ""
        )
        .map(([productId, product]) => ({
          productId: productId,
          newEANCode: product.newEANCode,
        }));

      // Replace with your actual API endpoint
      axios
        .post(process.env.REACT_APP_PATH + "/role4/updateProduct", {
          orderId: orderDetails._id,
          products: changedProducts,
        })
        .then((res) => {
          if (res.data.success) {
            // Show success message
            setSnackbar({
              open: true,
              message: "Products successfully replaced",
              severity: "success",
            });
            // Optionally refresh order details
            handleOrderSearch();

            // Reset modified products
            setModifiedProducts({});
          } else {
            alert(res.data?.message || "Someting went wrong");
          }
        })
        .catch((error) => {
          alert("Someting went wrong");
        });
    } catch (err) {
      console.error("Error replacing products:", err);
      setSnackbar({
        open: true,
        message: "Failed to replace products. Please try again.",
        severity: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Close snackbar
  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h5" component="h1" gutterBottom>
          Product Replacement in Order
        </Typography>

        {/* Order Search Form */}
        <Box
          component="div"
          sx={{ display: "flex", mb: 3 }}
        >
          <TextField
            label="Order ID"
            variant="outlined"
            value={orderId}
            onChange={(e) => setOrderId(e.target.value)}
            sx={{ flexGrow: 1, mr: 2 }}
            placeholder="Enter order ID"
            required
          />
          <Button
            type="submit"
            variant="contained"
            startIcon={<Search />}
            disabled={loading}
            onClick={handleOrderSearch}
          >
            {loading ? <CircularProgress size={24} /> : "Search"}
          </Button>
        </Box>

        {error && (
          <Typography color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}

        {/* Order Details */}
        {orderDetails && (
          <>
            <Card sx={{ mb: 4 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Order Details
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Customer Name
                    </Typography>
                    <Typography variant="body1">
                      {orderDetails.name}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Phone Number
                    </Typography>
                    <Typography variant="body1">
                      {orderDetails.phoneNumber}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Created At
                    </Typography>
                    <Typography variant="body1">
                      {formatDate(orderDetails.createdAt)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Total Amount
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {orderDetails?.totalPayableAmount.toFixed(2)}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Products Table with Editable EAN Codes */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography variant="h6">Order Products</Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<Save />}
                disabled={submitting || !hasModifiedProducts()}
                onClick={handleSubmitChanges}
              >
                {submitting ? <CircularProgress size={24} /> : "Submit Changes"}
              </Button>
            </Box>

            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Product ID</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell align="center">Quantity</TableCell>
                    <TableCell>Current EAN Code</TableCell>
                    <TableCell>New EAN Code</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orderDetails.products.map((product) => (
                    <TableRow key={product.productId}>
                      <TableCell>{product.productId}</TableCell>
                      <TableCell>{product.name}</TableCell>
                      <TableCell align="center">{product.quantity}</TableCell>
                      <TableCell>{product.EANCode}</TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          placeholder="Enter new EAN code"
                          value={modifiedProducts[product.productId]?.newEANCode || ""}
                          onChange={(e) =>
                            handleEANChange(product.productId, e.target.value)
                          }
                          variant="outlined"
                          fullWidth
                          error={
                            modifiedProducts[product.productId]?.newEANCode === ""
                          }
                          helperText={
                            modifiedProducts[product.productId]?.newEANCode === ""
                              ? "EAN code cannot be empty"
                              : ""
                          }
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Paper>

      {/* Success/Error Notification */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ProductReplacementPanel;
